import './AiModalCardFooter.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import jsPDF from 'jspdf';
import { patchAiLogRating } from '@sonar-software/sonar-core';

import { App, Button, Space, Tooltip } from 'antd';
import { DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import React, { useCallback, useState } from 'react';

export interface AiModalCardFooterProps {
	aiRequestLogId: string;
	allowDownload?: boolean;
	cardRef?: React.RefObject<HTMLDivElement>;
	onCancel?: () => void;
}

export const AiModalCardFooter: React.FC<AiModalCardFooterProps> = ({
	aiRequestLogId,
	allowDownload,
	cardRef,
	onCancel
}) => {
	const [ rating, setRating ] = useState<boolean | undefined>();
	const { notification } = App.useApp();

	const handleClick = useCallback((target: boolean) => {
		if (rating === target) {
			setRating(undefined);
			patchAiLogRating({
				aiRequestLogId,
				rating: null
			});

			return;
		}

		setRating(target);

		patchAiLogRating({
			aiRequestLogId,
			rating: target
		});

		notification.open({
			message: COPY_SPECIFIC.THANKS_FOR_YOUR_FEEDBACK
		});
	}, [ aiRequestLogId, notification, rating ]);

	const handleSaveClick = useCallback(() => {
		const bodyElement = cardRef?.current?.childNodes[ 1 ] as HTMLElement;

		if (!bodyElement) {
			return;
		}

		// eslint-disable-next-line new-cap
		const pdf = new jsPDF();

		pdf.setFont('Public Sans');
		pdf.setFontSize(1);

		pdf.html(bodyElement, {
			autoPaging: 'text',
			callback(doc) {
				doc.save('arovy-summary.pdf');
			},
			html2canvas: { scale: 0.25 },
			margin: 8
		});
	}, [ cardRef ]);

	if (!aiRequestLogId) {
		return null;
	}

	return (
		<Space
			align='center'
			className={ classNames('ai-modal-card-footer', { 'with-download': allowDownload }) }
			size='small'>
			<Tooltip
				placement='bottom'
				title={ COPY_SPECIFIC.NOT_HELPFUL }>
				<Button
					className={ classNames('rating-button', {
						active: rating !== undefined && !rating,
						inactive: rating !== undefined && rating
					}) }
					color='default'
					icon={ <DislikeOutlined /> }
					onClick={ () => handleClick(false) }
					variant='link' />
			</Tooltip>
			<Tooltip
				placement='bottom'
				title={ COPY_SPECIFIC.HELPFUL }>
				<Button
					className={ classNames('rating-button', {
						active: rating !== undefined && rating,
						inactive: rating !== undefined && !rating
					}) }
					color='default'
					icon={ <LikeOutlined /> }
					onClick={ () => handleClick(true) }
					variant='link' />
			</Tooltip>
			{ allowDownload &&
				<Button
					onClick={ onCancel }
					type='default'>
					{ COPY_SPECIFIC.CANCEL }
				</Button> }
			{ allowDownload &&
				<Button
					onClick={ handleSaveClick }
					type='primary'>
					{ COPY_SPECIFIC.SAVE_AS_PDF }
				</Button> }
		</Space>
	);
};
